/**
 * Compact Vendors list — Phase 15.6.
 *
 * Columns (44 px sticky-header rows):
 *   ☐ | Square avatar+Name+Email | Type pill | Locations
 *     | Billed | Paid (green) | Balance (red owed / green advance)
 *     | Next due (red overdue / amber <7d) | Status pill | Actions (hover)
 *
 * Page header carries a red "Outstanding ₹X" badge.
 * Totals row: Billed | Paid | Outstanding.
 * Filter chips: All | Overdue | Hotels | Transport | Airlines | Activities.
 * Export respects active filters.
 *
 * Data sources:
 *   - GET /api/vendors/rollup        — totalFinal (Billed) per supplierRef
 *   - GET /api/accounting/vendors    — canonical contact (email/phone)
 *   - paid + due fields are computed from db.expenses (paymentStatus,
 *     dueDate) so Balance / Status are accurate without new endpoints.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Search, Eye, MessageCircle, Banknote } from 'lucide-react';
import FilterChips from '@/components/list/FilterChips';
import BulkBar from '@/components/list/BulkBar';
import Pagination from '@/components/list/Pagination';
import ExportButton from '@/components/list/ExportButton';
import SortableHeader from '@/components/list/SortableHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtINR = (n) => '\u20B9' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return '—'; }
};
const daysFromNow = (s) => {
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86400000);
};

// Vendor type pill colors. Reused for the type column. Phase 16 added
// DMC / Consolidator / Tour Operator — common Indian outbound supply tiers.
const TYPE_PILL = {
  hotel:           'bg-purple-50 text-purple-700 border-purple-200',
  transport:       'bg-blue-50 text-blue-700 border-blue-200',
  airline:         'bg-indigo-50 text-indigo-700 border-indigo-200',
  flight:          'bg-indigo-50 text-indigo-700 border-indigo-200',
  activity:        'bg-amber-50 text-amber-700 border-amber-200',
  visa:            'bg-emerald-50 text-emerald-700 border-emerald-200',
  guide:           'bg-teal-50 text-teal-700 border-teal-200',
  dmc:             'bg-rose-50 text-rose-700 border-rose-200',
  consolidator:    'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  'tour-operator': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  default:         'bg-slate-100 text-slate-700 border-slate-200',
};

const STATUS_PILL = {
  Overdue: 'bg-rose-50 text-rose-700 border-rose-200',
  'Due soon': 'bg-amber-50 text-amber-700 border-amber-200',
  Advance:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  Settled:  'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const VendorsList = () => {
  const [rollup, setRollup] = useState([]);
  const [canonical, setCanonical] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [sort, setSort] = useState({ key: 'billed', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      axios.get(`${BACKEND_URL}/api/vendors/rollup`, { withCredentials: true }).catch(() => ({ data: { items: [] } })),
      axios.get(`${BACKEND_URL}/api/accounting/vendors`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${BACKEND_URL}/api/accounting/expenses`, { withCredentials: true }).catch(() => ({ data: [] })),
    ])
      .then(([rl, vl, ex]) => {
        if (!alive) return;
        setRollup((rl.data && rl.data.items) || []);
        setCanonical(Array.isArray(vl.data) ? vl.data : []);
        setExpenses(Array.isArray(ex.data) ? ex.data : []);
      })
      .catch(() => toast.error('Failed to load vendors'))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  useEffect(() => { setSelected(new Set()); setPage(1); }, [typeFilter, search, sort, pageSize]);

  // Merge: pick CANONICAL vendors as the spine (so newly-created vendors
  // with no spend yet still appear), then decorate each with the rollup
  // and expense data we have on file. Category is sourced from the
  // canonical `vendor.type` (authoritative — that's what the operator
  // picked when creating the vendor), falling back to rollup.category
  // only when the canonical type is empty. Normalise spaces / case so
  // the filter pills always match (e.g. "Tour Operator" → "tour-operator").
  const normaliseType = (raw) => {
    if (!raw) return '';
    return String(raw).trim().toLowerCase().replace(/\s+/g, '-');
  };

  const rows = useMemo(() => {
    // Expense rollup per vendorId — sum paid, find earliest unpaid dueDate.
    const expByVendor = {};
    for (const e of expenses) {
      if ((e.paymentStatus || '').toLowerCase() === 'cancelled') continue;
      const k = e.vendorId || e.vendorName;
      if (!k) continue;
      if (!expByVendor[k]) expByVendor[k] = { paid: 0, billed: 0, nextDue: null };
      const amt = Number(e.totalAmount || 0);
      expByVendor[k].billed += amt;
      if ((e.paymentStatus || '').toLowerCase() === 'paid') {
        expByVendor[k].paid += amt;
      } else if (e.dueDate && (!expByVendor[k].nextDue || e.dueDate < expByVendor[k].nextDue)) {
        expByVendor[k].nextDue = e.dueDate;
      }
    }

    // Index rollup by vendorId + by supplierRef so we can hydrate either way.
    const rollupById = {};
    const rollupByRef = {};
    for (const r of rollup) {
      if (r.vendorId) rollupById[r.vendorId] = r;
      if (r.supplierRef) rollupByRef[r.supplierRef] = r;
    }
    // Track which rollup entries we've consumed so we can surface any
    // orphan rollup rows (cost-items without a canonical vendor doc) at
    // the bottom of the list — otherwise spend silently disappears.
    const consumedRefs = new Set();

    const out = canonical.map((c) => {
      const vid = c.vendorId || c.id || c.vendorName;
      const rl = rollupById[vid] || rollupByRef[vid] || {};
      if (rl.supplierRef) consumedRefs.add(rl.supplierRef);
      if (rl.vendorId) consumedRefs.add(rl.vendorId);
      const exp = expByVendor[vid] || {};
      const billed = Number(rl.totalFinal || exp.billed || 0);
      const paid = Number(exp.paid || 0);
      const balance = billed - paid;
      const nextDue = exp.nextDue || null;
      const daysToDue = nextDue ? daysFromNow(nextDue) : null;

      let status = 'Settled';
      if (balance < 0) status = 'Advance';
      else if (balance > 0 && daysToDue !== null && daysToDue < 0) status = 'Overdue';
      else if (balance > 0 && daysToDue !== null && daysToDue <= 7) status = 'Due soon';
      else if (balance === 0) status = 'Settled';
      else status = 'Due soon';

      return {
        vendorId: vid,
        name: c.vendorName || c.name || rl.vendorName || rl.supplierRef || '(Unnamed)',
        email: c.email || '',
        phone: c.phone || '',
        type: normaliseType(c.type || rl.category),
        locations: Array.isArray(rl.destinations) ? rl.destinations.filter(Boolean).join(', ') : '',
        billed,
        paid,
        balance,
        nextDue,
        daysToDue,
        status,
      };
    });

    // Append orphan rollup rows (legacy cost-items that never got a
    // canonical vendor doc created). These keep historical spend visible.
    for (const r of rollup) {
      const ref = r.vendorId || r.supplierRef;
      if (!ref || consumedRefs.has(ref)) continue;
      const exp = expByVendor[ref] || {};
      const billed = Number(r.totalFinal || exp.billed || 0);
      const paid = Number(exp.paid || 0);
      const balance = billed - paid;
      const nextDue = exp.nextDue || null;
      const daysToDue = nextDue ? daysFromNow(nextDue) : null;
      let status = 'Settled';
      if (balance < 0) status = 'Advance';
      else if (balance > 0 && daysToDue !== null && daysToDue < 0) status = 'Overdue';
      else if (balance > 0 && daysToDue !== null && daysToDue <= 7) status = 'Due soon';
      else if (balance === 0) status = 'Settled';
      else status = 'Due soon';
      out.push({
        vendorId: ref,
        name: r.vendorName || r.supplierRef || '(Unlinked)',
        email: '',
        phone: '',
        type: normaliseType(r.category),
        locations: Array.isArray(r.destinations) ? r.destinations.filter(Boolean).join(', ') : '',
        billed,
        paid,
        balance,
        nextDue,
        daysToDue,
        status,
      });
    }
    return out;
  }, [rollup, canonical, expenses]);

  const counts = useMemo(() => {
    const c = { all: rows.length, overdue: 0, hotel: 0, transport: 0, airline: 0, activity: 0, dmc: 0, consolidator: 0, 'tour-operator': 0 };
    for (const r of rows) {
      if (r.status === 'Overdue') c.overdue += 1;
      if (c[r.type] !== undefined) c[r.type] += 1;
    }
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    let out = rows;
    if (typeFilter !== 'all') {
      if (typeFilter === 'overdue') out = out.filter((r) => r.status === 'Overdue');
      else out = out.filter((r) => r.type === typeFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      out = out.filter((r) =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        (r.locations || '').toLowerCase().includes(q),
      );
    }
    return out;
  }, [rows, typeFilter, search]);

  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    const dir = sort.dir === 'asc' ? 1 : -1;
    const get = (r) => {
      if (sort.key === 'name') return (r.name || '').toLowerCase();
      if (sort.key === 'billed') return r.billed;
      if (sort.key === 'paid') return r.paid;
      if (sort.key === 'balance') return r.balance;
      if (sort.key === 'nextDue') return r.nextDue || '';
      return r[sort.key] || '';
    };
    return [...filtered].sort((a, b) => (get(a) < get(b) ? -1 : get(a) > get(b) ? 1 : 0) * dir);
  }, [filtered, sort]);

  const totals = useMemo(() => sorted.reduce((acc, r) => ({
    billed: acc.billed + (r.billed || 0),
    paid: acc.paid + (r.paid || 0),
    outstanding: acc.outstanding + Math.max(0, r.balance || 0),
  }), { billed: 0, paid: 0, outstanding: 0 }), [sorted]);

  const pageRows = useMemo(() => sorted.slice((page - 1) * pageSize, page * pageSize), [sorted, page, pageSize]);

  const chips = [
    { value: 'all',           label: 'All',          count: counts.all },
    { value: 'overdue',       label: 'Overdue',      count: counts.overdue },
    { value: 'hotel',         label: 'Hotels',       count: counts.hotel },
    { value: 'transport',     label: 'Transport',    count: counts.transport },
    { value: 'airline',       label: 'Airlines',     count: counts.airline },
    { value: 'activity',      label: 'Activities',   count: counts.activity },
    { value: 'dmc',           label: 'DMC',          count: counts.dmc },
    { value: 'consolidator',  label: 'Consolidator', count: counts.consolidator },
    { value: 'tour-operator', label: 'Tour Operator', count: counts['tour-operator'] },
  ];

  const exportColumns = [
    { key: 'name', label: 'Vendor' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'type', label: 'Type' },
    { key: 'locations', label: 'Locations' },
    { key: 'billed', label: 'Billed (₹)' },
    { key: 'paid', label: 'Paid (₹)' },
    { key: 'balance', label: 'Balance (₹)' },
    { key: 'nextDue', label: 'Next due', format: (r) => r.nextDue || '' },
    { key: 'status', label: 'Status' },
  ];

  const toggle = (id) => {
    setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  const toggleAll = () => {
    if (selected.size === pageRows.length) setSelected(new Set());
    else setSelected(new Set(pageRows.map((r) => r.vendorId)));
  };

  return (
    <div className="space-y-4" data-testid="vendors-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Vendors</h1>
            <p className="text-sm text-slate-500 mt-0.5">{loading ? 'Loading…' : `${rows.length} supplier${rows.length === 1 ? '' : 's'}`}</p>
          </div>
          {totals.outstanding > 0 ? (
            <span className="inline-flex items-center text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-2.5 py-1" data-testid="outstanding-badge">
              Outstanding {fmtINR(totals.outstanding)}
            </span>
          ) : null}
        </div>
        <ExportButton filename="vendors" columns={exportColumns} rows={sorted} testid="vendors-export" />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative" style={{ width: 280 }}>
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vendor, email, location…"
            className="pl-8 h-9 rounded-[7px]"
            data-testid="vendors-search"
          />
        </div>
        <FilterChips chips={chips} value={typeFilter} onChange={setTypeFilter} testidPrefix="vendor-chip" />
      </div>

      <BulkBar
        count={selected.size}
        actions={[
          { key: 'remind', label: 'WhatsApp reminder' },
          { key: 'pay', label: 'Mark paid', tone: 'emerald' },
        ]}
        onAction={(k) => toast.info(`Bulk ${k} — coming soon`)}
        onClear={() => setSelected(new Set())}
        testid="vendors-bulk-bar"
      />

      <VendorsTable
        pageRows={pageRows} loading={loading}
        sort={sort} setSort={setSort}
        selected={selected} toggle={toggle} toggleAll={toggleAll}
      />

      <VendorTotals totals={totals} />

      <Pagination
        page={page} pageSize={pageSize} total={sorted.length}
        onPageChange={setPage} onPageSizeChange={setPageSize}
        testid="vendors-pagination"
      />
    </div>
  );
};

const VendorsTable = ({ pageRows, loading, sort, setSort, selected, toggle, toggleAll }) => {
  const allChecked = pageRows.length > 0 && pageRows.every((r) => selected.has(r.vendorId));
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid="vendors-table">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 sticky top-0 z-10">
            <tr className="text-left">
              <th className="w-10 px-3 py-2">
                <input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="vendor-check-all" />
              </th>
              <th className="px-3 py-2"><SortableHeader label="Vendor" columnKey="name" sort={sort} setSort={setSort} /></th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Type</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Locations</th>
              <th className="px-3 py-2 text-right"><SortableHeader label="Billed" columnKey="billed" sort={sort} setSort={setSort} align="right" /></th>
              <th className="px-3 py-2 text-right"><SortableHeader label="Paid" columnKey="paid" sort={sort} setSort={setSort} align="right" /></th>
              <th className="px-3 py-2 text-right"><SortableHeader label="Balance" columnKey="balance" sort={sort} setSort={setSort} align="right" /></th>
              <th className="px-3 py-2"><SortableHeader label="Next due" columnKey="nextDue" sort={sort} setSort={setSort} /></th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Status</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={10} className="py-8 text-center text-slate-400 text-xs">Loading…</td></tr>
            ) : pageRows.length === 0 ? (
              <tr><td colSpan={10} className="py-8 text-center text-slate-400 text-xs">No vendors match the current filters.</td></tr>
            ) : pageRows.map((r) => <VendorRow key={r.vendorId} r={r} checked={selected.has(r.vendorId)} toggle={toggle} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const VendorRow = ({ r, checked, toggle }) => {
  const initials = (r.name || '?').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
  const typeClass = TYPE_PILL[r.type] || TYPE_PILL.default;
  const balanceColor = r.balance > 0 ? 'text-rose-700' : r.balance < 0 ? 'text-emerald-700' : 'text-slate-500';
  const dueColor = (r.daysToDue !== null && r.daysToDue < 0) ? 'text-rose-700 font-semibold'
    : (r.daysToDue !== null && r.daysToDue <= 7) ? 'text-amber-700' : 'text-slate-600';
  const waUrl = r.phone ? `https://wa.me/${String(r.phone).replace(/\D/g, '')}` : null;

  return (
    <tr className="group hover:bg-slate-50" style={{ height: 44 }} data-testid={`vendor-row-${r.vendorId}`}>
      <td className="px-3 py-1.5">
        <input type="checkbox" checked={checked} onChange={() => toggle(r.vendorId)} data-testid={`vendor-check-${r.vendorId}`} />
      </td>
      <td className="px-3 py-1.5">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-[6px] bg-slate-100 text-slate-700 text-[10px] font-semibold flex items-center justify-center">{initials}</div>
          <div className="min-w-0">
            <div className="font-medium text-slate-900 truncate">{r.name || '—'}</div>
            <div className="text-[10px] text-slate-400 truncate">{r.email || ''}</div>
          </div>
        </div>
      </td>
      <td className="px-3 py-1.5">
        {r.type ? (
          <span className={`inline-flex items-center text-[10px] font-medium border rounded-full px-2 py-0.5 capitalize ${typeClass}`}>
            {r.type}
          </span>
        ) : <span className="text-slate-300">—</span>}
      </td>
      <td className="px-3 py-1.5 text-slate-600 truncate max-w-[180px]">{r.locations || '—'}</td>
      <td className="px-3 py-1.5 text-right font-mono">{fmtINR(r.billed)}</td>
      <td className="px-3 py-1.5 text-right font-mono text-emerald-700">{fmtINR(r.paid)}</td>
      <td className={`px-3 py-1.5 text-right font-mono font-semibold ${balanceColor}`}>{fmtINR(Math.abs(r.balance))}</td>
      <td className={`px-3 py-1.5 ${dueColor}`}>{r.nextDue ? fmtDate(r.nextDue) : '—'}</td>
      <td className="px-3 py-1.5">
        <span className={`inline-flex items-center text-[10px] font-medium border rounded-full px-2 py-0.5 ${STATUS_PILL[r.status] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
          {r.status}
        </span>
      </td>
      <td className="px-3 py-1.5 text-right">
        <VendorActions vendorId={r.vendorId} waUrl={waUrl} />
      </td>
    </tr>
  );
};

const VendorActions = ({ vendorId, waUrl }) => (
  <div className="inline-flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
    <button type="button" title="Pay" data-testid={`vendor-pay-${vendorId}`} className="h-[26px] w-[26px] inline-flex items-center justify-center rounded-[6px] text-slate-600 hover:bg-slate-100">
      <Banknote className="h-3.5 w-3.5" />
    </button>
    {waUrl ? (
      <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid={`vendor-wa-${vendorId}`}>
        <button type="button" title="WhatsApp reminder" className="h-[26px] w-[26px] inline-flex items-center justify-center rounded-[6px] text-slate-600 hover:bg-slate-100">
          <MessageCircle className="h-3.5 w-3.5" />
        </button>
      </a>
    ) : null}
    <Link to={`/ledger?party_type=vendor&party_id=${encodeURIComponent(vendorId)}`} data-testid={`vendor-view-${vendorId}`}>
      <button type="button" title="View ledger" className="h-[26px] w-[26px] inline-flex items-center justify-center rounded-[6px] text-slate-600 hover:bg-slate-100">
        <Eye className="h-3.5 w-3.5" />
      </button>
    </Link>
  </div>
);

const VendorTotals = ({ totals }) => (
  <div className="flex items-center justify-end gap-6 text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg" data-testid="vendors-totals">
    <span><span className="text-slate-500">Billed</span> <span className="font-mono font-semibold ml-1">{fmtINR(totals.billed)}</span></span>
    <span><span className="text-slate-500">Paid</span> <span className="font-mono font-semibold ml-1 text-emerald-700">{fmtINR(totals.paid)}</span></span>
    <span><span className="text-slate-500">Outstanding</span> <span className="font-mono font-bold ml-1 text-rose-700">{fmtINR(totals.outstanding)}</span></span>
  </div>
);

export default VendorsList;
